import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Check, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";

// The /apply hub's inbox control: an envelope with an unread badge that opens a peek at the newest
// employer mail. Every managed address the candidate has ever been issued — the primary and every
// alias — feeds this ONE inbox, because an employer may hold any of them.
//
// Deliberately read-only and small: it is a glance, not a mail client. Clicking a message opens the
// full inbox on it.

const POLL_MS = 60000;          // employer mail arrives minutes apart; polling is plenty
const PEEK = 6;

const AV = ["#E8A400", "#0075BE", "#B4131E", "#0B7D3E", "#7C3AED", "#0D9488", "#2563EB", "#DB2777"];
const avatarColor = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV[h % AV.length];
};
const initials = (s) =>
  (s || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function InboxPeek() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [data, setData] = useState(null);      // null until the panel has been opened once
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  const pollUnread = useCallback(() => {
    api.get("/apply/inbox-unread")
      .then(({ data: d }) => setUnread(d.unread || 0))
      .catch(() => {});
  }, []);

  // Poll on an interval AND on window focus — coming back to the tab is exactly when someone wants
  // the count to be right.
  useEffect(() => {
    pollUnread();
    const t = setInterval(pollUnread, POLL_MS);
    window.addEventListener("focus", pollUnread);
    return () => { clearInterval(t); window.removeEventListener("focus", pollUnread); };
  }, [pollUnread]);

  const load = useCallback(() => {
    setBusy(true);
    api.get("/apply/inbox")
      .then(({ data: d }) => { setData(d); setUnread(d.unread || 0); })
      .catch(() => setData({ emails: [] }))
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Close on outside click / Escape, and keep focus sane for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const openMessage = (id) => { setOpen(false); navigate(`/apply/inbox?open=${encodeURIComponent(id)}`); };

  const markAllRead = async () => {
    try {
      await api.post("/apply/inbox/read-all");
      setUnread(0);
      setData((d) => (d ? { ...d, emails: (d.emails || []).map((e) => ({ ...e, read: true })) } : d));
    } catch { /* leave the badge alone rather than lie about it */ }
  };

  const emails = (data?.emails || []).slice(0, PEEK);

  return (
    <div className="relative flex-shrink-0" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? `Inbox, ${unread} unread` : "Inbox"}
        className={`relative inline-flex items-center gap-2 pl-2 pr-3 py-2 rounded-2xl text-[13.5px] font-semibold
          transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${open ? "shadow-md -translate-y-0.5" : ""}`}
        style={{ background: "#0025D812", border: "1px solid #0025D824", color: "#0F172A" }}>
        <span className="grid place-items-center w-7 h-7 rounded-xl flex-shrink-0" style={{ background: "#0025D822" }}>
          <Mail className="w-4 h-4" style={{ color: "#0025D8" }} />
        </span>
        <span className="hidden sm:inline">Inbox</span>
        {unread > 0 && (
          <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full leading-none tabular-nums text-white"
            style={{ background: "#0025D8" }}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Recent employer mail"
          className="absolute right-0 mt-2 w-[min(380px,calc(100vw-32px))] rounded-2xl border border-border bg-white shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="text-[13px] font-bold text-[#0F172A]">
              Inbox {unread > 0 && <span className="text-muted-foreground font-semibold">· {unread} new</span>}
            </div>
            {unread > 0 && (
              <button type="button" onClick={markAllRead}
                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0025D8] hover:underline">
                <Check className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {busy && !data && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground px-4 py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            )}
            {!busy && emails.length === 0 && (
              <div className="px-4 py-8 text-center">
                <div className="text-sm font-semibold text-[#0F172A]">No employer mail yet</div>
                <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                  Confirmations and replies sent to your apply address will land here.
                </p>
              </div>
            )}
            {emails.map((e) => {
              const who = e.company || e.from || "Unknown sender";
              return (
                <button key={e.email_id} type="button" onClick={() => openMessage(e.email_id)}
                  className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-[#F8FAFC] transition-colors border-b border-border/60 last:border-b-0">
                  <span className="grid place-items-center w-8 h-8 rounded-xl text-[11px] font-bold text-white flex-shrink-0 mt-0.5"
                    style={{ background: avatarColor(who) }}>
                    {initials(who)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={`truncate text-[13px] ${e.read ? "font-semibold text-[#0F172A]" : "font-extrabold text-[#0F172A]"}`}>
                        {who}
                      </span>
                      <span className="ml-auto text-[11px] text-muted-foreground tabular-nums flex-shrink-0">{timeAgo(e.received_at)}</span>
                      {!e.read && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#0025D8" }} />}
                    </span>
                    <span className={`block truncate text-[12.5px] mt-0.5 ${e.read ? "text-muted-foreground" : "text-[#0F172A]"}`}>
                      {e.subject || "(no subject)"}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <button type="button" onClick={() => { setOpen(false); navigate("/apply/inbox"); }}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-[12.5px] font-semibold text-[#0025D8] hover:bg-[#F8FAFC] border-t border-border transition-colors">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
